import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackContext } from '@sg/garnish';
import type { PaymentMethodCard } from '@sg/graphql-schema';

import { useDeletePaymentMethodCardMutation } from './graphql/DeletePaymentMethod.generated';

export function useDeletePaymentMethod(props: DeletePaymentMethodProps) {
  const { onSuccess } = props;

  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const [deletePaymentMethodCardResponse, deletePaymentMethodCardMutation] =
    useDeletePaymentMethodCardMutation();

  const deletePaymentMethod = useCallback(
    async (paymentMethod: PaymentMethodCard) => {
      const paymentMethodId = paymentMethod.id;

      const response = await deletePaymentMethodCardMutation({
        input: { id: paymentMethodId },
      });

      const result = response.data?.deletePaymentMethodCard;
      const isSuccess =
        result?.__typename === 'PaymentMethodCard' && result?.id === paymentMethodId;

      if (isSuccess) {
        addNoticeBanner({
          text: formatMessage(messages.creditCardFormRemoveSuccess),
          palette: 'success',
        });

        onSuccess();

        return;
      }

      const userError = formatMessage(messages.creditCardFormErrorDelete);

      addNoticeBanner({ text: userError, palette: 'caution' });
    },
    [deletePaymentMethodCardMutation, formatMessage, addNoticeBanner, onSuccess],
  );

  return {
    deletePaymentMethod,
    isDeleting: deletePaymentMethodCardResponse.fetching,
  };
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  creditCardFormRemoveSuccess: {
    defaultMessage: 'Payment method successfully removed.',
    description: 'Credit Card Form | Remove | Success',
  },
  creditCardFormErrorDelete: {
    defaultMessage: `Oops, we couldn't remove your card, please try again.`,
    description: 'Credit Card Form | Error | Delete',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type DeletePaymentMethodProps = Readonly<{
  onSuccess: () => void;
}>;
